import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import DeductionDialog from "../components/DeductionDialog";
import ExpenseRowIcons from "../components/ExpenseRowIcons";
import {
  Banknote, CheckCircle2, RotateCcw, Send, ExternalLink, Loader2,
  FileSearch, ChevronDown, ChevronUp, Download, Eye, ShieldCheck, ShieldAlert,
} from "lucide-react";

const STATUS_TABS = [
  { key: "all", label: "All", filter: "pending,charged,failed,sent_to_tms" },
  { key: "pending", label: "Pending", filter: "pending" },
  { key: "charged", label: "Charged", filter: "charged" },
  { key: "sent_to_tms", label: "Sent to TMS", filter: "sent_to_tms" },
  { key: "failed", label: "Failed", filter: "failed" },
];

const STATUS_BADGE = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 ring-amber-200" },
  charged: { label: "Charged", cls: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
  sent_to_tms: { label: "Sent to TMS", cls: "bg-sky-100 text-sky-800 ring-sky-200" },
  failed: { label: "Failed", cls: "bg-rose-100 text-rose-800 ring-rose-200" },
  not_applicable: { label: "—", cls: "bg-slate-100 text-slate-500 ring-slate-200" },
};

function fmtMoney(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return "—"; }
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span
      data-testid={`dc-status-badge-${status}`}
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${s.cls}`}>
      {s.label}
    </span>
  );
}

const VERIFY_STYLES = {
  loading: { cls: "bg-slate-50 text-slate-600 ring-slate-200", label: "Checking…", row: "" },
  ok:       { cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", label: "OK",         row: "" },
  paid:     { cls: "bg-amber-50 text-amber-800 ring-amber-200",     label: "Paid",        row: "bg-amber-50/60" },
  mismatch: { cls: "bg-yellow-100 text-yellow-900 ring-yellow-300", label: "Amt mismatch",row: "bg-yellow-50/70" },
  deleted:  { cls: "bg-rose-100 text-rose-800 ring-rose-200",       label: "Deleted",     row: "bg-rose-50/70" },
  error:    { cls: "bg-orange-50 text-orange-800 ring-orange-200",  label: "Error",       row: "bg-orange-50/50" },
};

function VerifyBadge({ entry, onClick }) {
  if (!entry) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  if (entry.loading) {
    const s = VERIFY_STYLES.loading;
    return (
      <span data-testid="dc-verify-loading"
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${s.cls}`}>
        <Loader2 className="h-3 w-3 animate-spin" /> {s.label}
      </span>
    );
  }
  const s = VERIFY_STYLES[entry.state] || VERIFY_STYLES.error;
  let title = "";
  if (entry.state === "mismatch") {
    const localD = ((entry.local_abs_cents ?? 0) / 100).toFixed(2);
    const alvysD = entry.alvys_abs_cents == null ? "—" : (entry.alvys_abs_cents / 100).toFixed(2);
    title = `Local $${localD} vs Alvys $${alvysD}`;
  } else if (entry.state === "deleted") {
    title = "Deduction not found in Alvys (404)";
  } else if (entry.state === "error") {
    title = entry.error || "Alvys lookup failed";
  } else if (entry.state === "paid") {
    title = "Alvys reports this deduction as paid";
  } else if (entry.state === "ok") {
    title = "Live data matches local record";
  }
  const Icon = entry.state === "ok" ? ShieldCheck
             : entry.state === "deleted" || entry.state === "error" ? ShieldAlert
             : null;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={`dc-verify-badge-${entry.state}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${s.cls} hover:opacity-80`}>
      {Icon && <Icon className="h-3 w-3" />}
      {s.label}
    </button>
  );
}

function DriverChips({ drivers }) {
  if (!drivers?.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {drivers.map((d) => (
        <span key={d.id} className="inline-flex rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
          {d.label || d.id}
        </span>
      ))}
    </div>
  );
}

export default function DriverCharges() {
  const { reload: reloadMe } = useAuth();
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [driverId, setDriverId] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [markOpen, setMarkOpen] = useState(false);
  const [markTarget, setMarkTarget] = useState(null); // expense id or "bulk"
  const [markForm, setMarkForm] = useState({ external_ref: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(null); // expense object

  // Deduction viewer modal (B)
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerRefs, setViewerRefs] = useState([]);
  const [viewerTitle, setViewerTitle] = useState("Alvys Deduction");

  // Audit panel (A)
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditDays, setAuditDays] = useState(30);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  // ref → { loading, state: "ok"|"paid"|"mismatch"|"deleted"|"error", alvys_abs_cents, error }
  const [auditVerify, setAuditVerify] = useState({});
  const [auditVerifying, setAuditVerifying] = useState(false);

  const openViewer = (refs, title) => {
    const list = Array.isArray(refs)
      ? refs.filter(Boolean)
      : (refs || "").split(",").map((s) => s.trim()).filter(Boolean);
    setViewerRefs(list);
    setViewerTitle(title || "Alvys Deduction");
    setViewerOpen(true);
  };

  const loadAudit = useCallback(async () => {
    setAuditLoading(true); setAuditError("");
    try {
      const r = await api.get("/driver-charges/audit", { params: { days: auditDays } });
      setAuditRows(r.data?.rows || []);
      setAuditVerify({});
    } catch (e) { setAuditError(extractError(e)); }
    finally { setAuditLoading(false); }
  }, [auditDays]);

  // Parallel Alvys verification with concurrency cap = 5.
  // For each audit row, GET /api/deductions/{ref} and classify the result.
  const verifyAuditRows = useCallback(async (rows) => {
    const refs = (rows || []).map((r) => r.deduction_ref).filter(Boolean);
    if (!refs.length) return;
    setAuditVerifying(true);
    // Seed loading state up front so all rows show spinners immediately.
    setAuditVerify((prev) => {
      const next = { ...prev };
      for (const ref of refs) next[ref] = { loading: true };
      return next;
    });
    // Index ref → local cents for mismatch comparison.
    const localByRef = new Map(rows.map((r) => [r.deduction_ref, Math.abs(r.amount_cents || 0)]));

    const CONCURRENCY = 5;
    let idx = 0;
    const worker = async () => {
      while (true) {
        const myIdx = idx++;
        if (myIdx >= refs.length) return;
        const ref = refs[myIdx];
        try {
          const r = await api.get(`/deductions/${encodeURIComponent(ref)}`);
          const d = r.data || {};
          // Alvys amount can be { Amount, Currency } or a number — normalize to abs cents.
          let alvysDollars;
          if (d.Amount && typeof d.Amount === "object" && "Amount" in d.Amount) {
            alvysDollars = Number(d.Amount.Amount);
          } else if (typeof d.Amount === "number") {
            alvysDollars = d.Amount;
          } else {
            alvysDollars = null;
          }
          const alvysAbsCents = alvysDollars == null ? null : Math.round(Math.abs(alvysDollars) * 100);
          const localCents = localByRef.get(ref);
          const paid = d.IsPaid === true;
          let state = "ok";
          if (paid) state = "paid";
          else if (alvysAbsCents != null && localCents != null && alvysAbsCents !== localCents) state = "mismatch";
          setAuditVerify((prev) => ({
            ...prev,
            [ref]: { loading: false, state, paid, alvys_abs_cents: alvysAbsCents, local_abs_cents: localCents },
          }));
        } catch (e) {
          const status = e?.response?.status;
          const msg = extractError(e) || "Alvys lookup failed";
          const state = status === 404 || /not found/i.test(msg) ? "deleted" : "error";
          setAuditVerify((prev) => ({
            ...prev,
            [ref]: { loading: false, state, error: msg, status },
          }));
        }
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, refs.length) }, () => worker()));
    } finally {
      setAuditVerifying(false);
    }
  }, []);

  useEffect(() => {
    if (auditOpen) loadAudit();
  }, [auditOpen, loadAudit]);

  // Auto-fire verification whenever audit rows refresh (panel open + data loaded).
  useEffect(() => {
    if (auditOpen && !auditLoading && auditRows.length > 0) {
      verifyAuditRows(auditRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditRows]);

  const exportAuditCsv = () => {
    if (!auditRows.length) return;
    const header = [
      "occurred_at", "vendor", "category", "driver_label", "driver_id",
      "amount", "expense_id", "deduction_ref", "charged_at", "charged_by",
    ];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of auditRows) {
      const amt = -Math.abs(r.amount_cents || 0) / 100;
      lines.push([
        r.occurred_at ? new Date(r.occurred_at).toISOString().slice(0, 10) : "",
        r.vendor,
        r.category_name || "",
        r.driver_label || "",
        r.driver_id || "",
        amt.toFixed(2),
        r.expense_id,
        r.deduction_ref,
        r.charged_at ? new Date(r.charged_at).toISOString() : "",
        r.charged_by_label || "",
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `driver-charges-audit-${auditDays}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filter = STATUS_TABS.find((t) => t.key === tab)?.filter || "pending";

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { status: filter };
      if (q) params.q = q;
      if (driverId) params.driver_id = driverId;
      const r = await api.get("/driver-charges", { params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setSelected(new Set());
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, [filter, q, driverId]);

  useEffect(() => { load(); }, [load]);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allEligibleIds = useMemo(
    () => items.filter((it) => it.driver_charge_status === "pending").map((it) => it.id),
    [items],
  );
  const selectAllOnPage = (checked) => {
    if (checked) setSelected(new Set(allEligibleIds));
    else setSelected(new Set());
  };

  const openMark = (expenseId) => {
    setMarkTarget(expenseId);
    setMarkForm({ external_ref: "", note: "" });
    setMarkOpen(true);
  };

  const submitMark = async () => {
    setBusy(true);
    try {
      if (markTarget === "bulk") {
        const ids = Array.from(selected);
        const r = await api.post("/driver-charges/bulk-mark", {
          ids, method: "manual", note: markForm.note || undefined,
        });
        toast.success(`Marked ${r.data.marked} as charged (${r.data.skipped} skipped)`);
      } else {
        await api.post(`/expenses/${markTarget}/mark-driver-charged`, {
          method: "manual",
          external_ref: markForm.external_ref || undefined,
          note: markForm.note || undefined,
        });
        toast.success("Marked as charged");
      }
      setMarkOpen(false);
      await load();
      await reloadMe();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const unmark = async (id) => {
    if (!window.confirm("Unmark this expense back to pending?")) return;
    try {
      await api.post(`/expenses/${id}/unmark-driver-charged`, {});
      toast.success("Reverted to pending");
      await load();
      await reloadMe();
    } catch (e) { toast.error(extractError(e)); }
  };

  const sendToTms = async (exp) => {
    setBusy(true);
    try {
      const r = await api.post(`/expenses/${exp.id}/send-to-tms`, {});
      const data = r.data;
      const okCount = (data.per_driver || []).filter((p) => p.ok).length;
      const failCount = (data.per_driver || []).filter((p) => !p.ok).length;
      if (data.overall_ok) {
        toast.success(`Sent to TMS: ${okCount} deduction(s) created`, {
          description: data.external_refs?.length ? `Refs: ${data.external_refs.join(", ")}` : undefined,
        });
      } else {
        const firstErr = (data.per_driver || []).find((p) => !p.ok)?.error || "unknown error";
        toast.error(`Send to TMS partially failed (${okCount} ok / ${failCount} failed)`, {
          description: firstErr.slice(0, 200),
        });
      }
      setConfirmSend(null);
      await load();
      await reloadMe();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="driver-charges-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            Driver Charges
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Work queue for driver chargebacks — {total} item{total === 1 ? "" : "s"}.
          </p>
        </div>
        <Link to="/reports/driver-chargebacks" className="text-sm font-medium text-[#0F1E3D] hover:underline" data-testid="dc-aggregate-link">
          View aggregate report →
        </Link>
      </div>

      {/* Audit panel — collapsible (A) */}
      <Card className="mb-4 border-slate-200" data-testid="dc-audit-panel">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAuditOpen((v) => !v)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800"
              data-testid="dc-audit-toggle">
              <FileSearch className="h-4 w-4" />
              Last {auditDays} days sent to Alvys
              {auditOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {auditOpen && (
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs text-slate-500">Window</Label>
                {[7, 30, 90].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setAuditDays(n)}
                    data-testid={`dc-audit-days-${n}`}
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${
                      auditDays === n
                        ? "bg-[#0F1E3D] text-white ring-[#0F1E3D]"
                        : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                    }`}>
                    {n}d
                  </button>
                ))}
                <Button size="sm" variant="outline" onClick={loadAudit}
                  disabled={auditLoading} data-testid="dc-audit-refresh">
                  {auditLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh"}
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => verifyAuditRows(auditRows)}
                  disabled={auditVerifying || auditLoading || auditRows.length === 0}
                  data-testid="dc-audit-verify-all">
                  {auditVerifying ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="mr-1 h-3 w-3" />}
                  Re-verify
                </Button>
                <Button size="sm" variant="outline" onClick={exportAuditCsv}
                  disabled={!auditRows.length} data-testid="dc-audit-export-csv">
                  <Download className="mr-1 h-3 w-3" /> Export CSV
                </Button>
              </div>
            )}
          </div>

          {auditOpen && (
            <div className="mt-3">
              {auditError && (
                <Alert variant="destructive" className="mb-2"><AlertDescription>{auditError}</AlertDescription></Alert>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="dc-audit-table">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-2">Occurred</th>
                      <th className="px-2 py-2">Vendor</th>
                      <th className="px-2 py-2">Driver</th>
                      <th className="px-2 py-2 text-right">Amount</th>
                      <th className="px-2 py-2">Deduction Ref</th>
                      <th className="px-2 py-2">Charged At</th>
                      <th className="px-2 py-2">Charged By</th>
                      <th className="px-2 py-2 text-right">Verify</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLoading ? (
                      <tr><td colSpan={8} className="px-2 py-6 text-center text-slate-500">
                        <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
                      </td></tr>
                    ) : auditRows.length === 0 ? (
                      <tr><td colSpan={8} className="px-2 py-6 text-center text-slate-500" data-testid="dc-audit-empty">
                        No deductions sent in this window.
                      </td></tr>
                    ) : auditRows.map((r) => {
                      const v = auditVerify[r.deduction_ref];
                      const rowTint = v && !v.loading ? (VERIFY_STYLES[v.state]?.row || "") : "";
                      return (
                      <tr key={r.deduction_ref} className={`border-t border-slate-100 ${rowTint}`} data-testid={`dc-audit-row-${r.deduction_ref}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.occurred_at)}</td>
                        <td className="px-2 py-1.5">
                          <Link to={`/expenses/${r.expense_id}`} className="text-slate-800 hover:underline">{r.vendor}</Link>
                        </td>
                        <td className="px-2 py-1.5 text-slate-700">{r.driver_label || r.driver_id || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-medium text-rose-700">
                          -{fmtMoney(Math.abs(r.amount_cents))}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500" title={r.deduction_ref}>
                          {r.deduction_ref.slice(0, 12)}…
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{fmtDate(r.charged_at)}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.charged_by_label || "—"}</td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <VerifyBadge
                              entry={v}
                              onClick={() => openViewer([r.deduction_ref], `Alvys Deduction — ${r.driver_label || r.driver_id || ""}`)}
                            />
                            <Button size="sm" variant="ghost"
                              onClick={() => openViewer([r.deduction_ref], `Alvys Deduction — ${r.driver_label || r.driver_id || ""}`)}
                              data-testid={`dc-audit-verify-${r.deduction_ref}`}
                              title="View full Alvys data">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <span data-testid="dc-audit-total">
                  {auditRows.length} deduction(s) — total {`-$${(auditRows.reduce((s, r) => s + Math.abs(r.amount_cents || 0), 0) / 100).toFixed(2)}`}
                </span>
                {auditRows.length > 0 && (() => {
                  const counts = { ok: 0, paid: 0, mismatch: 0, deleted: 0, error: 0, loading: 0, pending: 0 };
                  for (const r of auditRows) {
                    const v = auditVerify[r.deduction_ref];
                    if (!v) counts.pending++;
                    else if (v.loading) counts.loading++;
                    else counts[v.state] = (counts[v.state] || 0) + 1;
                  }
                  const issues = counts.paid + counts.mismatch + counts.deleted + counts.error;
                  return (
                    <span data-testid="dc-audit-verify-summary" className="flex items-center gap-2">
                      <ShieldCheck className="h-3 w-3 text-emerald-600" />
                      <span className="text-emerald-700">{counts.ok} OK</span>
                      {counts.paid > 0 && <span className="text-amber-700">· {counts.paid} paid</span>}
                      {counts.mismatch > 0 && <span className="text-yellow-700">· {counts.mismatch} mismatch</span>}
                      {counts.deleted > 0 && <span className="text-rose-700">· {counts.deleted} deleted</span>}
                      {counts.error > 0 && <span className="text-orange-700">· {counts.error} error</span>}
                      {(counts.loading + counts.pending) > 0 && <span className="text-slate-500">· {counts.loading + counts.pending} checking…</span>}
                      {issues === 0 && (counts.loading + counts.pending) === 0 && <span className="text-emerald-700">· all clear</span>}
                    </span>
                  );
                })()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="dc-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`dc-tab-${t.key}`}
            className={`rounded-full px-3 py-1 text-sm font-medium ring-1 transition-colors ${
              tab === t.key
                ? "bg-[#0F1E3D] text-white ring-[#0F1E3D]"
                : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs text-slate-500">Vendor</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Vendor contains…"
            className="w-56" data-testid="dc-filter-vendor" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Driver ID</Label>
          <Input value={driverId} onChange={(e) => setDriverId(e.target.value)}
            placeholder="exact id"
            className="w-44" data-testid="dc-filter-driver" />
        </div>
        <Button variant="outline" onClick={load} data-testid="dc-refresh-btn">Refresh</Button>
        {selected.size > 0 && (
          <Button
            onClick={() => { setMarkTarget("bulk"); setMarkForm({ external_ref: "", note: "" }); setMarkOpen(true); }}
            className="ml-auto bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
            data-testid="dc-bulk-mark-btn">
            <CheckCircle2 className="mr-1 h-4 w-4" /> Mark {selected.size} as charged
          </Button>
        )}
      </div>

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === allEligibleIds.length && allEligibleIds.length > 0}
                      onCheckedChange={selectAllOnPage}
                      data-testid="dc-select-all" />
                  </th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Vendor</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Drivers</th>
                  <th className="px-3 py-2">Worker</th>
                  <th className="px-3 py-2">Card</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Charged at</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
                  </td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500" data-testid="dc-empty-state">
                    No driver charges in this view.
                  </td></tr>
                ) : items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`dc-row-${it.id}`}>
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={selected.has(it.id)}
                        onCheckedChange={() => toggleRow(it.id)}
                        disabled={it.driver_charge_status !== "pending"}
                        data-testid={`dc-row-select-${it.id}`} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(it.occurred_at)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <Link to={`/expenses/${it.id}`} className="font-medium text-slate-900 hover:underline">{it.vendor}</Link>
                        <ExpenseRowIcons expense={it} size="sm" />
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{fmtMoney(it.amount_cents)}</td>
                    <td className="px-3 py-2"><DriverChips drivers={it.drivers} /></td>
                    <td className="px-3 py-2 text-slate-600">{it.user_name || it.user_email}</td>
                    <td className="px-3 py-2 text-slate-600">{it.card_label || "—"}</td>
                    <td className="px-3 py-2"><StatusBadge status={it.driver_charge_status} /></td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(it.driver_charged_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {it.driver_charge_status === "pending" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => openMark(it.id)}
                              data-testid={`dc-mark-btn-${it.id}`} title="Mark as charged">
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmSend(it)}
                              data-testid={`dc-send-btn-${it.id}`} title="Send to TMS">
                              <Send className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {(it.driver_charge_status === "charged" || it.driver_charge_status === "failed") && (
                          <Button size="sm" variant="ghost" onClick={() => unmark(it.id)}
                            data-testid={`dc-unmark-btn-${it.id}`} title="Unmark / revert to pending">
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        {it.driver_charge_status === "sent_to_tms" && it.driver_charge_external_ref && (
                          <Button size="sm" variant="ghost"
                            onClick={() => openViewer(it.driver_charge_external_ref, `Alvys Deduction — ${it.vendor}`)}
                            data-testid={`dc-view-alvys-btn-${it.id}`}
                            title="View in Alvys">
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Link to={`/expenses/${it.id}`} title="View details">
                          <Button size="sm" variant="ghost"><ExternalLink className="h-4 w-4" /></Button>
                        </Link>
                      </div>
                      {it.driver_charge_status === "failed" && it.driver_charge_last_error && (
                        <p className="mt-1 text-right text-[10px] text-rose-600" title={it.driver_charge_last_error}>
                          {it.driver_charge_last_error.slice(0, 80)}
                        </p>
                      )}
                      {it.driver_charge_external_ref && (
                        <p className="mt-1 text-right text-[10px] text-slate-500" title={it.driver_charge_external_ref}>
                          ref: {it.driver_charge_external_ref.slice(0, 24)}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Manual mark dialog (single + bulk) */}
      <Dialog open={markOpen} onOpenChange={setMarkOpen}>
        <DialogContent data-testid="dc-mark-dialog">
          <DialogHeader>
            <DialogTitle>
              <Banknote className="mr-1 inline h-4 w-4 -translate-y-0.5" />
              {markTarget === "bulk" ? `Mark ${selected.size} as charged (manual)` : "Mark as charged (manual)"}
            </DialogTitle>
            <DialogDescription>
              Records the chargeback as completed in our books without calling Alvys.
              Optionally add an external reference (e.g. a settlement id) and an internal note.
            </DialogDescription>
          </DialogHeader>
          {markTarget !== "bulk" && (
            <div className="space-y-2">
              <Label>External reference</Label>
              <Input value={markForm.external_ref}
                onChange={(e) => setMarkForm((f) => ({ ...f, external_ref: e.target.value }))}
                placeholder="Optional"
                data-testid="dc-mark-external-ref" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea value={markForm.note}
              onChange={(e) => setMarkForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Optional"
              rows={3}
              data-testid="dc-mark-note" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMarkOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitMark} disabled={busy}
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
              data-testid="dc-mark-submit-btn">
              {busy ? "Marking…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send-to-TMS confirm dialog */}
      <Dialog open={!!confirmSend} onOpenChange={(o) => !o && setConfirmSend(null)}>
        <DialogContent data-testid="dc-send-dialog">
          <DialogHeader>
            <DialogTitle>
              <Send className="mr-1 inline h-4 w-4 -translate-y-0.5" />
              Send to TMS
            </DialogTitle>
            <DialogDescription>
              {confirmSend && (
                <>This will create <span className="font-semibold">{confirmSend.drivers?.length || 0}</span> deduction(s)
                  in Alvys for <span className="font-semibold">{(confirmSend.drivers || []).map((d) => d.label || d.id).join(", ") || "—"}</span>.
                  Amount: <span className="font-semibold">{fmtMoney(confirmSend.amount_cents)}</span>. Continue?</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmSend(null)} disabled={busy}>Cancel</Button>
            <Button onClick={() => sendToTms(confirmSend)} disabled={busy}
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
              data-testid="dc-send-submit-btn">
              {busy ? "Sending…" : "Send to TMS"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deduction viewer modal (B + A's Verify) */}
      <DeductionDialog
        open={viewerOpen}
        onOpenChange={(o) => { setViewerOpen(o); if (!o) setViewerRefs([]); }}
        refs={viewerRefs}
        title={viewerTitle}
      />
    </div>
  );
}
